import React, { useCallback, useEffect, useState } from 'react';
import { X, Check, Send } from 'lucide-react';
import SectionHead from './SectionHead';
import { OPTIONS } from '../data/bargeOptions';

const HULLS = [
  { id: '8x24', label: "8' × 24'" },
  { id: '10x30', label: "10' × 30'" },
];

const ENGINES = [
  { id: 'single-suzuki-115', label: 'Single Suzuki 115' },
  { id: 'twin-suzuki-115', label: 'Twin Suzuki 115' },
  { id: 'custom', label: 'Custom' },
];

export default function BargeConfigurator({ open: openProp, onClose: onCloseProp } = {}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : internalOpen;
  const onClose = useCallback(
    () => {
      if (controlled) {
        onCloseProp && onCloseProp();
        return;
      }
      setInternalOpen(false);
      // If we opened via the modal shortcut and pushed /build onto the
      // history, roll the URL back to whatever the user was reading.
      if (typeof window !== 'undefined' && window.location.pathname === '/build') {
        // If /build was the direct entry point (no previous state), stay put.
        if (window.history.state && window.history.state.__fromModal) {
          window.history.back();
        }
      }
    },
    [controlled, onCloseProp]
  );

  const [hull, setHull] = useState(null);
  const [engine, setEngine] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState(() => new Set());

  // Listen for a global custom event so any button on the page can open the modal.
  useEffect(() => {
    if (controlled) return;
    const openHandler = () => setInternalOpen(true);
    window.addEventListener('open-barge-configurator', openHandler);
    return () => window.removeEventListener('open-barge-configurator', openHandler);
  }, [controlled]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const toggleOption = (label) => {
    setSelectedOptions((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const handleSubmit = () => {
    // Collect summary + prefill the contact form message, then scroll to it.
    const hullText = HULLS.find((h) => h.id === hull)?.label || 'Not selected';
    const engineText = ENGINES.find((e) => e.id === engine)?.label || 'Not selected';
    const optionList = [...selectedOptions];
    const optionText = optionList.length
      ? optionList.map((o) => `  • ${o}`).join('\n')
      : '  (none selected)';

    const message =
      `Barge Configuration\n` +
      `\nHull size: ${hullText}` +
      `\nEngine: ${engineText}` +
      `\n\nSelected options:\n${optionText}\n`;

    const textarea = document.querySelector('#quote-form textarea');
    if (textarea) {
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLTextAreaElement.prototype,
        'value'
      ).set;
      nativeInputValueSetter.call(textarea, message);
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
    }
    onClose();
    setTimeout(() => {
      document.getElementById('quote-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/85 backdrop-blur-sm overscroll-contain"
      data-testid="barge-configurator-modal"
      data-lenis-prevent
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full min-h-full bg-black">
        <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div className="absolute -top-20 right-0 w-[320px] sm:w-[480px] h-[320px] sm:h-[480px] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

        {/* Sticky top bar with close */}
        <div className="sticky top-0 z-10 bg-black/85 backdrop-blur-sm border-b border-white/5">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-4 flex items-center justify-between">
            <div className="font-tech text-cyan-300 text-[10px] sm:text-[11px] uppercase tracking-[0.28em]">
              Build My Barge
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close configurator"
              data-testid="close-configurator-btn"
              className="w-10 h-10 rounded-md border border-white/10 hover:border-cyan-400/50 text-white/80 hover:text-cyan-300 flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-12 sm:py-16">
          <SectionHead
            eyebrow="Customize Your HDPE Barge"
            title="Build My Barge"
            body="Pick your hull size, engine, and everything else you want on your new Absolute HDPE Barge. Then, when you&apos;re ready, we&apos;ll copy every specification over to our quote request form for you to submit and get a response within 24 hours."
          />

          {/* Hull size */}
          <div className="mt-10 sm:mt-14">
            <div className="flex items-center gap-3 mb-4 sm:mb-5">
              <span className="h-px w-8 bg-cyan-400" />
              <h3 className="font-display text-white text-base sm:text-lg font-semibold tracking-wide">
                Choose Your Hull Size
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {HULLS.map((h) => (
                <SelectableBox
                  key={h.id}
                  active={hull === h.id}
                  onClick={() => setHull(h.id)}
                  testid={`hull-${h.id}`}
                >
                  <span className="text-white text-base sm:text-lg font-semibold">{h.label}</span>
                </SelectableBox>
              ))}
            </div>
          </div>

          {/* Engine */}
          <div className="mt-10 sm:mt-12">
            <div className="flex items-center gap-3 mb-4 sm:mb-5">
              <span className="h-px w-8 bg-cyan-400" />
              <h3 className="font-display text-white text-base sm:text-lg font-semibold tracking-wide">
                Choose Your Engine
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {ENGINES.map((e) => (
                <SelectableBox
                  key={e.id}
                  active={engine === e.id}
                  onClick={() => setEngine(e.id)}
                  testid={`engine-${e.id}`}
                >
                  <span className="text-white text-base sm:text-lg font-semibold">{e.label}</span>
                </SelectableBox>
              ))}
            </div>
          </div>

          {/* Available Options */}
          <div className="mt-10 sm:mt-14">
            <div className="flex items-center gap-3 mb-4 sm:mb-5">
              <span className="h-px w-8 bg-cyan-400" />
              <h3 className="font-display text-white text-base sm:text-lg font-semibold tracking-wide">
                Choose Your Specific Features
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {OPTIONS.map((o) => {
                const Icon = o.icon;
                const active = selectedOptions.has(o.label);
                return (
                  <button
                    type="button"
                    key={o.label}
                    onClick={() => toggleOption(o.label)}
                    data-testid={`option-toggle-${o.label}`}
                    aria-pressed={active}
                    className={`panel rounded-md px-4 py-3.5 flex items-center gap-3 text-left transition-colors ${
                      active ? 'border-cyan-400/60' : 'hover:border-cyan-400/30'
                    }`}
                  >
                    <span
                      className={`w-5 h-5 rounded-[4px] border flex items-center justify-center shrink-0 transition-colors ${
                        active
                          ? 'bg-cyan-400 border-cyan-400 text-black'
                          : 'border-cyan-400/60 bg-transparent'
                      }`}
                    >
                      {active && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                    </span>
                    <Icon className="w-4 h-4 text-cyan-300 shrink-0" />
                    <span className="text-zinc-200 text-sm">{o.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Submit */}
          <div className="mt-10 sm:mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6 panel rounded-xl p-6 sm:p-7">
            <div>
              <div className="font-tech text-cyan-300 text-[11px] uppercase tracking-[0.32em] mb-1">
                Ready to Send?
              </div>
              <p className="text-zinc-400 text-sm">
                Copy my build specifications and let me add notes before sending.
              </p>
            </div>
            <button
              type="button"
              onClick={handleSubmit}
              data-testid="submit-configuration-btn"
              className="btn-teal px-5 sm:px-6 py-3 rounded-md inline-flex items-center gap-2 text-xs sm:text-sm uppercase tracking-[0.16em] sm:tracking-[0.18em] whitespace-nowrap"
            >
              <Send className="w-4 h-4" /> Send My Build
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SelectableBox({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`panel rounded-md px-5 py-4 flex items-center gap-3 text-left transition-colors ${
        active ? 'border-cyan-400/60' : 'hover:border-cyan-400/30'
      }`}
    >
      <span
        className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
          active
            ? 'bg-cyan-400 border-cyan-400 text-black'
            : 'border-cyan-400/60 bg-transparent'
        }`}
      >
        {active && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
      </span>
      {children}
    </button>
  );
}
